/**
 * DR_META — capture au dessin.
 *
 * Estampe l'identité de création sur chaque entité fraîchement dessinée par DR :
 *   - `entityUid`  : identité stable, merge-safe (UUID, pas un compteur séquentiel)
 *   - `createdAt`  : epoch ms → ordre chronologique réel du fil « Activité »
 *   - `kind`       : nature CAD réelle (EntityType — text/polyline/mleader/...)
 *
 * Appelé depuis `BaseAddEntityCommand.execute()` (le point de passage unique des
 * ajouts d'entités). Idempotent : si l'entité porte déjà un DR_META, on n'y touche
 * pas → `entityUid`/`createdAt` sont préservés à travers redo / ré-exécution.
 *
 * Best-effort : ne doit jamais casser le dessin. Le n° de photo n'est PAS dupliqué
 * ici — il est déjà porté par le XData DR_PHOTO_MARKER et lu à l'affichage.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import { findEntityByHandle } from "../core/oda";
import { deleteObj } from "../core/delete";
import { writeEntityMeta, readEntityMeta } from "./xdata";
import { getEntityType, EntityType } from "../entities/detection";
import {
  readMarkerPhotoIds,
  writeMarkerPhotoIds,
} from "../photomarker/photo-link";
import { v4 as uuid } from "uuid";
import { Logger } from "@/logger";

/** Estampe entityUid + createdAt + role sur les entités qui n'ont pas encore de DR_META. */
export function stampCreationMeta(
  viewer: DRDrawingViewer,
  handles: string[],
): void {
  const module = viewer.module;
  const db = viewer.database;
  if (!db || handles.length === 0) return;

  const now = Date.now();
  // Désordre actif au dessin (les draggers stylent l'entité avec ce note_config).
  // Le calque n'est PAS discriminant (Fissure & Faïençage partagent un calque) →
  // on stocke le NOM du note_config pour résoudre label/couleur via la légende.
  const defectName = viewer.activeNoteConfig?.name ?? null;

  for (const h of handles) {
    const id = findEntityByHandle(module, db, h);
    if (!id) continue;
    try {
      if (readEntityMeta(module, id) === null) {
        writeEntityMeta(module, id, {
          entityUid: uuid(),
          kind: getEntityType(module, id),
          markerNumber: null,
          createdAt: now,
          defectName,
          background: false,
          observationId: null,
        });
      }
    } catch (e) {
      // La capture est best-effort : un souci de meta ne doit jamais casser le dessin.
      Logger.captureException(e, { site: "stampCreationMeta", handle: h });
    }
    deleteObj(id);
  }
}

/**
 * Copie d'entités (deepCloneObjects) : le clone hérite du XData de la source, donc
 * de son `entityUid`. Deux entités d'un même plan partageraient alors la même
 * identité — l'oplog attribuerait l'activité de la copie à l'original et la fusion
 * de versions les confondrait. On attribue ici une identité NEUVE au clone
 * (`entityUid` + `createdAt`) ; le reste du meta (désordre, observation, n° de
 * marqueur) est conservé. Un clone d'entité de fond de plan est une vraie création
 * DR : `background` repasse à `false`. La copie d'un marqueur ne garde pas ses
 * photos : on copie souvent un marqueur pour en poser un nouveau.
 *
 * À appeler AVANT l'exécution des commandes d'ajout (`stampCreationMeta`, idempotent,
 * laisserait sinon l'identité copiée en place). Un clone sans meta n'est pas touché :
 * `stampCreationMeta` lui en donnera une.
 */
export function renewClonedIdentity(
  viewer: DRDrawingViewer,
  handles: string[],
): void {
  const module = viewer.module;
  const db = viewer.database;
  if (!db || handles.length === 0) return;

  const now = Date.now();
  for (const h of handles) {
    const id = findEntityByHandle(module, db, h);
    if (!id) continue;
    try {
      const meta = readEntityMeta(module, id);
      if (meta) {
        writeEntityMeta(module, id, {
          ...meta,
          entityUid: uuid(),
          createdAt: now,
          background: false,
        });
      }
      if (readMarkerPhotoIds(module, id).length) {
        writeMarkerPhotoIds(module, id, []);
      }
    } catch (e) {
      // best-effort : un souci de meta ne doit jamais casser la copie.
      Logger.captureException(e, { site: "renewClonedIdentity", handle: h });
    }
    deleteObj(id);
  }
}

/**
 * Conversion texte → MLeader : transfère le DR_META du texte source vers le MLeader
 * (continuité entityUid / createdAt / defectName, kind MLeader). Ainsi le repère hérite
 * de l'identité du texte. Si la source n'a pas de meta (dessin antérieur), capture une
 * identité neuve sur le MLeader.
 *
 * Non destructif pour la source : le texte masqué garde son meta mais est ignoré
 * partout car il est sur `UNDO_LAYER` (filtré). Sur undo, c'est le texte qui
 * redevient visible et porte l'identité — les deux ont le même meta, un seul est
 * sur un vrai calque à la fois.
 */
export function transferMetaOnConvert(
  viewer: DRDrawingViewer,
  fromHandle: string,
  toHandle: string,
): void {
  const module = viewer.module;
  const db = viewer.database;
  if (!db) return;
  const fromId = findEntityByHandle(module, db, fromHandle);
  const toId = findEntityByHandle(module, db, toHandle);
  try {
    if (!toId) return;
    const src = fromId ? readEntityMeta(module, fromId) : null;
    if (src) {
      writeEntityMeta(module, toId, { ...src, kind: EntityType.MLeader });
    } else if (readEntityMeta(module, toId) === null) {
      writeEntityMeta(module, toId, {
        entityUid: uuid(),
        kind: EntityType.MLeader,
        markerNumber: null,
        createdAt: Date.now(),
        defectName: viewer.activeNoteConfig?.name ?? null,
        background: false,
        observationId: null,
      });
    }
  } catch (e) {
    // best-effort : la conversion reste valable sans meta.
    Logger.captureException(e, {
      site: "transferMetaOnConvert",
      fromHandle,
      toHandle,
    });
  } finally {
    if (fromId) deleteObj(fromId);
    if (toId) deleteObj(toId);
  }
}
